import React, { useState, useCallback } from 'react';
import './aimage.css';
import { 
    Box, 
    TextField, 
    Button, 
    CircularProgress, 
    Snackbar, 
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Paper,
    IconButton,
    Tooltip,
    Fade,
    Typography,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Chip,
    Container,
    Divider
} from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BrushIcon from '@mui/icons-material/Brush';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { query as generateImageHF } from '../components/huggingFace';
import { generateImageWithGoogleImagen, downloadImage, validatePrompt, getApiStatus } from '../services/googleImagenService';

function Aimage() {
    const [prompt, setPrompt] = useState('');
    const [generatedImages, setGeneratedImages] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState('');
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [lastQuery, setLastQuery] = useState('');
    const [imageCount, setImageCount] = useState(1);
    const [aspectRatio, setAspectRatio] = useState('1:1');
    const [selectedModel, setSelectedModel] = useState('google'); // 'huggingface' or 'google' - Google as default

    const showNotification = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleModelChange = (event) => {
        const newModel = event.target.value;
        setSelectedModel(newModel);
        // Reset image count to 1 for Google Imagen (it only supports 1 image)
        if (newModel === 'google') {
            setImageCount(1);
        }
    };

    const generateImages = async () => {
        // Validate prompt first
        const validation = validatePrompt(prompt);
        if (!validation.valid) {
            setError(validation.error);
            showNotification(validation.error, 'error');
            return;
        }

        const cleanPrompt = validation.cleanPrompt;

        if (cleanPrompt === lastQuery && generatedImages.length > 0) {
            showNotification('You just used this prompt! Try something different.', 'warning');
            return;
        }

        setIsGenerating(true);
        setError('');
        setGenerationProgress('');
        setLastQuery(cleanPrompt);

        console.log(`Starting image generation with ${selectedModel} model`);
        console.log(`Prompt: "${cleanPrompt}"`);
        console.log(`Aspect Ratio: ${aspectRatio}`);
        console.log(`Image Count: ${imageCount}`);

        // Show API status for debugging
        const apiStatus = getApiStatus();
        console.log('API Status:', apiStatus);

        try {
            let imageUrls = [];
            
            if (selectedModel === 'google') {
                // Use Google Imagen API with comprehensive fallbacks
                console.log('Using ChatBox AI with comprehensive fallbacks...');
                showNotification('Generating image with ChatBox AI...', 'info');
                setGenerationProgress('Initializing ChatBox AI...');
                
                const result = await generateImageWithGoogleImagen(cleanPrompt, aspectRatio);
                console.log('ChatBox AI result:', result);
                
                // Check if it's a placeholder
                if (result.isPlaceholder) {
                    showNotification('Generated placeholder due to API issues. All image services are currently unavailable.', 'warning');
                    setGenerationProgress('Generated creative placeholder due to service unavailability');
                } else {
                    showNotification(`Image generated successfully with ${result.generatedBy || 'ChatBox AI'}!`, 'success');
                    setGenerationProgress('Image generated successfully!');
                }
                
                imageUrls = [{
                    url: result.url,
                    prompt: cleanPrompt,
                    model: result.model || 'ChatBox AI',
                    aspectRatio: aspectRatio,
                    dimensions: result.dimensions || { width: 1024, height: 1024 },
                    generatedBy: result.generatedBy,
                    isPlaceholder: result.isPlaceholder || false
                }];
            } else {
                // Use Hugging Face SDXL
                console.log('Using Hugging Face SDXL API...');
                setGenerationProgress(`Generating ${imageCount} unique image(s)...`);
                showNotification(`Generating ${imageCount} unique image(s) with Hugging Face SDXL...`, 'info');
                
                // Add progress tracking for multiple images
                if (imageCount > 1) {
                    setGenerationProgress(`Starting generation of ${imageCount} unique images...`);
                    showNotification(`Starting generation of ${imageCount} unique images. Please wait...`, 'info');
                }
                
                const result = await generateImageHF(
                    cleanPrompt, 
                    null, 
                    aspectRatio, 
                    imageCount,
                    (progress) => setGenerationProgress(progress) // Progress callback
                );
                console.log('Hugging Face result:', result);
                
                if (!result.images || result.images.length === 0) {
                    throw new Error('No images generated from Hugging Face API');
                }

                // Verify we got unique images
                console.log(`Successfully generated ${result.images.length} unique images`);
                setGenerationProgress(`Processing ${result.images.length} generated images...`);
                
                imageUrls = await Promise.all(
                    result.images.map(async (blob, index) => {
                        // Create unique URL for each blob
                        const url = URL.createObjectURL(blob);
                        return {
                            url: url,
                            prompt: cleanPrompt,
                            model: 'Hugging Face SDXL',
                            aspectRatio: aspectRatio,
                            index: index + 1,
                            blob: blob,
                            uniqueId: `${Date.now()}-${index}` // Add unique identifier
                        };
                    })
                );
                console.log(`Generated ${imageUrls.length} unique images with Hugging Face`);
                
                if (imageCount > 1) {
                    showNotification(`Successfully generated ${imageUrls.length} unique images with Hugging Face SDXL!`, 'success');
                } else {
                    showNotification(`Image generated successfully with Hugging Face SDXL!`, 'success');
                }
            }
            
            setGeneratedImages(imageUrls);
        } catch (error) {
            console.error('Generation error:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                selectedModel,
                prompt: cleanPrompt,
                aspectRatio,
                imageCount
            });
            
            let errorMessage = error.message || 'Failed to generate image. Please try again.';
            
            // Provide more helpful error messages
            if (errorMessage.includes('quota') || errorMessage.includes('billing')) {
                errorMessage = 'API quota exceeded. Please try again later or switch to a different model.';
            } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
                errorMessage = 'Network error. Please check your internet connection and try again.';
            } else if (errorMessage.includes('timeout')) {
                errorMessage = 'Request timed out. Please try again with a shorter prompt.';
            }
            
            setError(errorMessage);
            
            // Show model-specific error messages
            if (selectedModel === 'google') {
                showNotification('ChatBox AI generation encountered an issue. All fallback services are also unavailable. Please try again later.', 'error');
            } else {
                showNotification('Hugging Face SDXL generation failed. Try switching to ChatBox AI or retry later.', 'error');
            }
        } finally {
            setIsGenerating(false);
            setGenerationProgress('');
        }
    };

    const handleDownload = async (imageUrl, index) => {
        try {
            showNotification('Starting download...', 'info');
            
            // Generate a unique filename with better naming
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const modelName = generatedImages[index]?.generatedBy || generatedImages[index]?.model || 'ai';
            const filename = `${modelName.toLowerCase().replace(/\s+/g, '-')}-image-${timestamp}.png`;
            
            // Use the enhanced downloadImage function
            const success = downloadImage(imageUrl, filename);
            
            if (success) {
                showNotification('Image downloaded successfully!', 'success');
            } else {
                showNotification('Download failed, but image opened in new tab', 'warning');
            }
        } catch (error) {
            console.error('Download error:', error);
            showNotification('Failed to download image. Please try right-clicking and saving manually.', 'error');
        }
    };

    return (
        <Box className="chatseaction1">
            <Container maxWidth="xl" className="ai-image-container">
                {/* Header Section */}
                <Box className="header-section">
                    <Box className="header-top">
                        <Link to="/" style={{ textDecoration: 'none' }}>
                            <Button 
                                startIcon={<ArrowBackIcon />}
                                className="back-button"
                                variant="outlined"
                            >
                                Back to Chat
                            </Button>
                        </Link>
                    </Box>
                    
                    <Box className="hero-section">
                        <Box className="hero-content">
                            <AutoAwesomeIcon className="hero-icon" />
                            <Typography variant="h1" className="hero-title">
                                AI Image Generator
                            </Typography>
                            <Typography variant="h6" className="hero-subtitle">
                                Transform your imagination into stunning visuals with advanced AI
                            </Typography>
                        </Box>
                        <Box className="hero-stats">
                            <Box className="stat-item">
                                <ImageIcon />
                                <Typography variant="body2">High Quality</Typography>
                            </Box>
                            <Box className="stat-item">
                                <BrushIcon />
                                <Typography variant="body2">Multiple Styles</Typography>
                            </Box>
                            <Box className="stat-item">
                                <PhotoLibraryIcon />
                                <Typography variant="body2">Various Formats</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Main Content */}
                <Box className="main-content">
                    {/* Input Panel */}
                    <Card className="input-panel" elevation={0}>
                        <CardContent className="input-content">
                            <Typography variant="h6" className="section-title" gutterBottom>
                                Describe Your Vision
                            </Typography>
                            
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                variant="outlined"
                                placeholder="Describe the image you want to create in detail... The more descriptive you are, the better the AI can bring your vision to life!"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                className="prompt-input"
                                InputProps={{
                                    startAdornment: <BrushIcon className="input-icon" />
                                }}
                            />

                            <Box className="controls-grid">
                                <FormControl className="control-item">
                                    <InputLabel>AI Model</InputLabel>
                                    <Select
                                        value={selectedModel}
                                        label="AI Model"
                                        onChange={handleModelChange}
                                    >
                                        <MenuItem value="google">
                                            <Box className="model-option">
                                                <AutoAwesomeIcon className="model-icon" />
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">ChatBox AI</Typography>
                                                    {/* <Typography variant="caption" color="textSecondary">Premium Quality</Typography> */}
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                        <MenuItem value="huggingface">
                                            <Box className="model-option">
                                                <ImageIcon className="model-icon" />
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">Other Platform</Typography>
                                                    {/* <Typography variant="caption" color="textSecondary">Fast Generation</Typography> */}
                                                </Box>
                                            </Box>
                                        </MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl className="control-item">
                                    <InputLabel>Number of Images</InputLabel>
                                    <Select
                                        value={imageCount}
                                        label="Number of Images"
                                        onChange={(e) => setImageCount(e.target.value)}
                                        disabled={selectedModel === 'google'}
                                    >
                                        <MenuItem value={1}>1 Image</MenuItem>
                                        <MenuItem value={2}>2 Images</MenuItem>
                                        <MenuItem value={4}>4 Images</MenuItem>
                                    </Select>
                                </FormControl>

                                <FormControl className="control-item">
                                    <InputLabel>Aspect Ratio</InputLabel>
                                    <Select
                                        value={aspectRatio}
                                        label="Aspect Ratio"
                                        onChange={(e) => setAspectRatio(e.target.value)}
                                    >
                                        <MenuItem value="1:1">Square (1:1)</MenuItem>
                                        <MenuItem value="16:9">Landscape (16:9)</MenuItem>
                                        <MenuItem value="9:16">Portrait (9:16)</MenuItem>
                                        <MenuItem value="3:4">Portrait (3:4)</MenuItem>
                                        <MenuItem value="4:3">Landscape (4:3)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <Button
                                variant="contained"
                                onClick={generateImages}
                                disabled={isGenerating || !prompt.trim()}
                                className="generate-button"
                                size="large"
                                fullWidth
                                startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                            >
                                {isGenerating ? (
                                    generationProgress || `Generating with ${selectedModel === 'google' ? 'ChatBox AI' : 'Other Platform'}...`
                                ) : (
                                    `Generate with ${selectedModel === 'google' ? 'ChatBox AI' : 'Other Platform'}`
                                )}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Error Display */}
                    {error && (
                        <Card className="error-card" elevation={0}>
                            <CardContent>
                                <Typography variant="body1" className="error-text">
                                    {error}
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    {/* Results Section */}
                    {generatedImages.length > 0 && (
                        <Box className="results-section">
                            <Box className="results-header">
                                <Typography variant="h5" className="results-title">
                                    Generated Images
                                </Typography>
                                <Chip 
                                    label={`${generatedImages.length} ${generatedImages.length === 1 ? 'Image' : 'Images'}`}
                                    className="results-count"
                                    variant="outlined"
                                />
                            </Box>
                            
                            <Divider className="results-divider" />

                            <Grid container spacing={3} className="images-grid">
                                {generatedImages.map((image, index) => (
                                    <Grid 
                                        item 
                                        xs={12} 
                                        sm={generatedImages.length > 1 ? 6 : 12} 
                                        md={generatedImages.length === 4 ? 6 : generatedImages.length === 2 ? 6 : 8}
                                        lg={generatedImages.length === 4 ? 6 : generatedImages.length === 2 ? 6 : 6}
                                        key={index}
                                        className="image-grid-item"
                                    >
                                        <Card className="image-card" elevation={0}>
                                            <Box className="image-container">
                                                <CardMedia
                                                    component="img"
                                                    image={image.url}
                                                    alt={image.prompt}
                                                    className="generated-image"
                                                />
                                                <Fade in={true}>
                                                    <Box className="image-overlay">
                                                        <Box className="overlay-actions">
                                                            <Tooltip title="Download Image" arrow placement="top">
                                                                <IconButton
                                                                    onClick={() => handleDownload(image.url, index)}
                                                                    className="overlay-button"
                                                                    size="large"
                                                                >
                                                                    <DownloadIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </Box>
                                                </Fade>
                                            </Box>
                                            
                                            <CardContent className="image-info">
                                                <Box className="image-details">
                                                    <Chip 
                                                        label={image.generatedBy || image.model}
                                                        size="small"
                                                        className={`model-chip ${image.isPlaceholder ? 'placeholder-chip' : ''}`}
                                                        variant="outlined"
                                                        color={image.isPlaceholder ? 'warning' : 'default'}
                                                    />
                                                    {image.isPlaceholder && (
                                                        <Chip 
                                                            label="Placeholder"
                                                            size="small"
                                                            color="warning"
                                                            variant="filled"
                                                            style={{ marginLeft: '8px' }}
                                                        />
                                                    )}
                                                    <Typography variant="caption" className="image-specs">
                                                        {image.aspectRatio}
                                                        {image.dimensions && (
                                                            <span> • {image.dimensions.width}×{image.dimensions.height}</span>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            </CardContent>
                                            
                                            <CardActions className="image-actions">
                                                <Button
                                                    size="small"
                                                    onClick={() => handleDownload(image.url, index)}
                                                    startIcon={<DownloadIcon />}
                                                    className="download-button"
                                                    fullWidth
                                                >
                                                    Download
                                                </Button>
                                            </CardActions>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </Box>

                {/* <Snackbar 
                    open={snackbar.open} 
                    autoHideDuration={6000} 
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert 
                        onClose={handleSnackbarClose} 
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar> */}
            </Container>
        </Box>
    );
}

export default Aimage;
